import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getDoctors, addDoctor, updateDoctor, deleteDoctor } from '../utils/api';

const NAVY = '#0d2137';
const GREEN = '#1a6b3c';
const RED = '#c41e3a';

const emptyForm = { name: '', specialty: '', room_number: '', timing: '', fees: '' };

export default function DoctorManagement() {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    getDoctors().then(r => setDoctors(r.data)).catch(() => toast.error('Failed to load doctors')).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const openAdd = () => { setForm(emptyForm); setEditId(null); setShowForm(true); };
  const openEdit = (doc) => { setForm({ name: doc.name, specialty: doc.specialty || '', room_number: doc.room_number || '', timing: doc.timing || '', fees: doc.fees || '' }); setEditId(doc.id); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Doctor name is required');
    if (!form.fees || isNaN(form.fees)) return toast.error('Please enter valid fees');
    setSaving(true);
    try {
      if (editId) {
        await updateDoctor(editId, form);
        toast.success('Doctor updated');
      } else {
        await addDoctor(form);
        toast.success('Doctor added');
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    }
    setSaving(false);
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete Dr. ${doc.name}? This cannot be undone.`)) return;
    try {
      await deleteDoctor(doc.id);
      toast.success('Doctor deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  return (
    <div style={{ padding: '32px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ color: NAVY, fontSize: 26, fontWeight: 900, marginBottom: 4 }}>Doctor Management</h1>
          <div style={{ color: '#64748b', fontSize: 14 }}>{doctors.length} doctor{doctors.length !== 1 ? 's' : ''} registered</div>
        </div>
        <button
          onClick={openAdd}
          style={{ padding: '10px 24px', background: GREEN, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
          + Add Doctor
        </button>
      </div>

      {/* Add/Edit form */}
      {showForm && (
        <div style={{ background: '#fff', borderRadius: 12, padding: '24px', marginBottom: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', border: `2px solid ${editId ? '#f59e0b' : GREEN}` }}>
          <h3 style={{ color: NAVY, marginBottom: 20, fontSize: 16, fontWeight: 800 }}>{editId ? 'Edit Doctor' : 'Add New Doctor'}</h3>
          <form onSubmit={handleSave}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {[['name', 'Doctor Name *', 'Dr. Muhammad Ali'], ['specialty', 'Specialty', 'General Medicine'], ['room_number', 'Room No.', 'Room 3'], ['timing', 'Timing', 'Mon-Sat 9am-2pm'], ['fees', 'Consultation Fees (PKR) *', '500']].map(([field, label, placeholder]) => (
                <div key={field} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{label}</label>
                  <input
                    value={form[field]}
                    onChange={set(field)}
                    placeholder={placeholder}
                    type={field === 'fees' ? 'number' : 'text'}
                    style={{ padding: '9px 12px', border: '1.5px solid #cbd5e1', borderRadius: 7, fontSize: 14, outline: 'none' }}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button type="submit" disabled={saving}
                style={{ padding: '10px 28px', background: editId ? '#f59e0b' : GREEN, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                {saving ? 'Saving...' : (editId ? 'Update' : 'Add Doctor')}
              </button>
              <button type="button" onClick={() => setShowForm(false)}
                style={{ padding: '10px 20px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Doctors table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Loading...</div>
      ) : doctors.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 15 }}>No doctors added yet. Click "+ Add Doctor" to start.</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: NAVY, color: '#fff' }}>
                {['#', 'Name', 'Specialty', 'Room', 'Timing', 'Fees', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 14px', fontSize: 12, fontWeight: 700, textAlign: 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {doctors.map((doc, i) => (
                <tr key={doc.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ padding: '12px 14px', fontSize: 12, color: '#94a3b8' }}>{i + 1}</td>
                  <td style={{ padding: '12px 14px', fontWeight: 700, color: NAVY }}>Dr. {doc.name}</td>
                  <td style={{ padding: '12px 14px', fontSize: 13 }}>{doc.specialty || '—'}</td>
                  <td style={{ padding: '12px 14px', fontSize: 13 }}>{doc.room_number || '—'}</td>
                  <td style={{ padding: '12px 14px', fontSize: 12, color: '#64748b' }}>{doc.timing || '—'}</td>
                  <td style={{ padding: '12px 14px', fontWeight: 700, color: GREEN }}>PKR {Number(doc.fees || 0).toLocaleString()}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => openEdit(doc)}
                        style={{ padding: '5px 14px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                        Edit
                      </button>
                      <button onClick={() => handleDelete(doc)}
                        style={{ padding: '5px 14px', background: RED, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
